import classNames from 'classnames'
import { NavArrowDown } from 'iconoir-react'
import { type ReactNode, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'

/**
 * A value that opens a picker on click. Weight and a dotted underline set it apart
 * from its grey label and say "this can change", without the chip chrome that
 * made the first version read as a row of dropdowns.
 */
const TRIGGER_CLASS = `h-7 gap-1 px-1 text-body-sm font-medium text-strong underline decoration-dotted decoration-trn-400 underline-offset-4 hover:bg-surface-muted hover:decoration-trn-700`

type Props = {
  label: string
  /** Nothing chosen yet: the label is a prompt, shown greyer. */
  muted?: boolean
  title?: string
  align?: `start` | `end`
  contentClassName?: string
  /** The popover body. Call `close` after a choice. */
  children: (close: () => void) => ReactNode
}

/**
 * The one popover-select shape every picker in the quick-create form shares:
 * trigger text with a chevron, a white popover, and a `close` handed to the body.
 * What goes inside (a searchable list, a static list, a calendar) is the caller's.
 */
export function PickerButton({
  label,
  muted,
  title,
  align = `end`,
  contentClassName = `w-[220px] p-0`,
  children,
}: Props) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon"
          size="xs"
          title={title}
          className={classNames(TRIGGER_CLASS, muted && `text-subtle`)}
        >
          <span className="leading-none">{label}</span>
          <NavArrowDown className="h-3 w-3 text-minimal" strokeWidth={2} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className={classNames(`bg-surface-raised`, contentClassName)}>
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  )
}

/** A grey label beside a picker, the row grammar used above and below the fold. */
export function OptionRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 text-body-sm">
      <span className="text-subtle">{label}</span>
      {children}
    </div>
  )
}
